(function createTitanScanTestRuntime() {
  'use strict';

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function createDialogBoundary(documentRef = document) {
    let activeDialog = null;
    let returnFocus = null;
    let cancelHandler = null;
    let isolationRecords = [];

    function restoreBackground() {
      isolationRecords.reverse().forEach(({ element, hadInert, ariaHidden }) => {
        if (!hadInert) element.removeAttribute('inert');
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      isolationRecords = [];
    }

    function isolateBackground(dialog) {
      let current = dialog;
      while (current && current !== documentRef.body) {
        const parent = current.parentElement;
        if (!parent) break;
        [...parent.children].forEach((sibling) => {
          if (sibling === current) return;
          isolationRecords.push({
            element: sibling,
            hadInert: sibling.hasAttribute('inert'),
            ariaHidden: sibling.getAttribute('aria-hidden'),
          });
          sibling.setAttribute('inert', '');
          sibling.setAttribute('aria-hidden', 'true');
        });
        current = parent;
      }
    }

    function focusableElements() {
      if (!activeDialog) return [];
      return [...activeDialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => element instanceof HTMLElement && !element.hidden,
      );
    }

    function handleKeydown(event) {
      if (!activeDialog) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancelHandler?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        activeDialog.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = documentRef.activeElement;
      if (!activeDialog.contains(active)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    function deactivate(options = {}) {
      const focusTarget = returnFocus;
      restoreBackground();
      activeDialog = null;
      returnFocus = null;
      cancelHandler = null;
      if (options.restoreFocus && focusTarget instanceof HTMLElement) {
        requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
    }

    documentRef.addEventListener('keydown', handleKeydown, true);
    return Object.freeze({
      activate(dialog, trigger, onCancel) {
        deactivate();
        if (!(dialog instanceof HTMLElement)) return;
        activeDialog = dialog;
        returnFocus = trigger instanceof HTMLElement ? trigger : null;
        cancelHandler = typeof onCancel === 'function' ? onCancel : null;
        isolateBackground(dialog);
      },
      deactivate,
      destroy() {
        deactivate();
        documentRef.removeEventListener('keydown', handleKeydown, true);
      },
    });
  }

  function createCountdownController(windowRef = window) {
    let timer = null;
    let generation = 0;
    let activeOverlay = null;
    let activeLive = null;

    function cancel() {
      generation += 1;
      windowRef.clearTimeout(timer);
      timer = null;
      if (activeOverlay) activeOverlay.hidden = true;
      if (activeLive) activeLive.textContent = '';
      activeOverlay = null;
      activeLive = null;
    }

    function start(options) {
      cancel();
      const { overlay, value, live, onComplete } = options || {};
      if (!overlay || !value || !live || typeof onComplete !== 'function') return false;
      const suppliedStep = Number(options.stepMs);
      const stepMs = Number.isFinite(suppliedStep) && suppliedStep >= 20 ? suppliedStep : 1000;
      const currentGeneration = generation;
      let count = 3;
      activeOverlay = overlay;
      activeLive = live;

      function render() {
        value.textContent = String(count);
        live.textContent = `開始まで${count}秒`;
      }

      function advance() {
        if (currentGeneration !== generation) return;
        if (count > 1) {
          count -= 1;
          render();
          timer = windowRef.setTimeout(advance, stepMs);
          return;
        }
        timer = null;
        overlay.hidden = true;
        live.textContent = 'スタート';
        activeOverlay = null;
        activeLive = null;
        onComplete();
      }

      overlay.hidden = false;
      render();
      timer = windowRef.setTimeout(advance, stepMs);
      return true;
    }

    return Object.freeze({ cancel, start });
  }

  function measureArena(arena, target) {
    const arenaRect = arena.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const style = window.getComputedStyle(arena);
    return Object.freeze({
      left: arenaRect.left,
      top: arenaRect.top,
      width: arenaRect.width,
      height: arenaRect.height,
      targetWidth: targetRect.width,
      targetHeight: targetRect.height,
      insetTop: Number.parseFloat(style.paddingTop) || 0,
      insetRight: Number.parseFloat(style.paddingRight) || 0,
      insetBottom: Number.parseFloat(style.paddingBottom) || 0,
      insetLeft: Number.parseFloat(style.paddingLeft) || 0,
    });
  }

  function nextTargetPosition(geometry, touchMode, random = Math.random) {
    const edge = touchMode ? 26 : 32;
    const minX = edge + geometry.insetLeft;
    const minY = edge + geometry.insetTop;
    const maxX = Math.max(
      minX,
      geometry.width - geometry.targetWidth - edge - geometry.insetRight,
    );
    const maxY = Math.max(
      minY,
      geometry.height - geometry.targetHeight - edge - geometry.insetBottom,
    );
    return Object.freeze({
      x: minX + random() * Math.max(0, maxX - minX),
      y: minY + random() * Math.max(0, maxY - minY),
    });
  }

  function localPoint(geometry, clientX, clientY) {
    return Object.freeze({ x: clientX - geometry.left, y: clientY - geometry.top });
  }

  function targetCenter(geometry, position) {
    return Object.freeze({
      x: position.x + geometry.targetWidth / 2,
      y: position.y + geometry.targetHeight / 2,
    });
  }

  function canStartInputTest(mode, viewportWidth, viewportHeight) {
    if (mode === 'touch') return true;
    return Number(viewportWidth) >= 1100 && Number(viewportHeight) >= 650;
  }

  window.TitanScanTestRuntime = Object.freeze({
    canStartInputTest,
    createCountdownController,
    createDialogBoundary,
    localPoint,
    measureArena,
    nextTargetPosition,
    targetCenter,
  });
})();
